'use client'

import type { Route } from 'next'
import { useRouter } from 'next/navigation'
import { Repeat } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import { Skeleton } from '@/components/ui/skeleton'
import { routes } from '@/config/routes'
import {
  CaseAnalysis,
  PendingRequestCard,
  toMockCaseRecord,
  toMockHoldings,
  toMockTrades,
  useCaseHoldings,
  useCaseTrades,
  useMyCaseForFlow,
} from '@/features/cases'
import { InvestorShell } from '@/features/investor/components/investor-shell'

/** Ported from `nivex-design/apps/nivex-web-v3/src/pages/dashboard/DashboardTrades.tsx`. */
export function InvestorTradesView() {
  const router = useRouter()
  const query = useMyCaseForFlow('trades')
  const holdingsQuery = useCaseHoldings(query.data?.id)
  const tradesQuery = useCaseTrades(query.data?.id, 1, 50)

  return (
    <InvestorShell
      active="trades"
      title="Trades"
      description="Read straight from your tradebook — no manual input."
      actions={
        query.summary?.advisorName ? (
          <Badge variant="secondary" className="hidden max-w-56 truncate sm:inline-flex">
            {query.summary.advisorName} is looking after this
          </Badge>
        ) : null
      }
    >
      {query.isListLoading || (query.summary?.caseStatus === 'published' && query.isLoading) ? (
        <Skeleton className="h-40 w-full" />
      ) : query.summary && query.summary.caseStatus !== 'published' ? (
        <PendingRequestCard
          caseRecord={{
            id: query.summary.id,
            flow: 'trades',
            reference: query.summary.reference,
            status: query.summary.caseStatus,
          }}
        />
      ) : query.summary && query.data ? (
        <CaseAnalysis
          caseRecord={toMockCaseRecord(query.data)}
          injectedHoldings={
            holdingsQuery.data ? toMockHoldings(holdingsQuery.data, query.data.id) : []
          }
          injectedTrades={
            tradesQuery.data ? toMockTrades(tradesQuery.data.items, query.data.id) : []
          }
        />
      ) : (
        <Empty className="border border-dashed py-10">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Repeat />
            </EmptyMedia>
            <EmptyTitle>Want us to audit your trades?</EmptyTitle>
            <EmptyDescription>
              Upload your tradebook and a financial expert will review your trading activity.
            </EmptyDescription>
          </EmptyHeader>
          <Button onClick={() => router.push(routes.investor.request('trades') as Route)}>
            Get started
          </Button>
        </Empty>
      )}
    </InvestorShell>
  )
}
